// PRD.320 — one composition layer for every prescription print path.
//
// WHY A SINGLE HOOK. Five production paths render `PrescriptionPrintLayout`
// (wizard, pharmacy mount, WhatsApp image, encounter tab, share modal). Giving
// each its own query chain would guarantee they drift — and the offscreen image
// path fails SILENTLY, rasterising a blank region rather than throwing. One
// hook means a section either works everywhere or nowhere.
//
// NO DUPLICATION. Every value is read from the domain that owns it. Nothing is
// copied into prescription storage to make it printable, so a printout can
// never disagree with the record it describes.
//
// Pass an empty `encounterId` for an encounterless prescription: every query
// no-ops and the composer returns the empty summary, so clinical sections
// simply disappear while patient + medication rendering continues.

import { useMemo } from 'react';
import {
  composeEncounterPrintClinical,
  groupTeethByDiagnosis,
  groupToothLinks,
  qClinicalFindingsByEncounter,
  qDentalInvestigationTeethByPatient,
  qDentalInvestigationsByEncounter,
  qDentalTreatmentRecordTeethByPatient,
  qDentalTreatmentRecordsByPatient,
  qDentalTreatmentTeethByPatient,
  qDentalTreatmentsByPatient,
  qDiagnosesByEncounter,
  qDiagnosisTeethByEncounter,
  qNoOp,
  type ClinicalFindingRow,
  type DentalInvestigationRow,
  type DentalTreatmentRecordRow,
  type DentalTreatmentRow,
  type DiagnosisToothRow,
  type EncounterPrintClinical,
  type PatientDiagnosisEntryRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../../adapters/powersync';

/**
 * Composed clinical content plus a readiness flag.
 *
 * `loaded` matters for ONE-SHOT consumers such as printing. An empty summary is
 * ambiguous — it means both "still loading" and "nothing recorded" — so a
 * printer that fires on first render can silently omit the Clinical Note. Live
 * screens do not care, because they re-render when the data lands.
 */
export interface EncounterPrintClinicalResult extends EncounterPrintClinical {
  loaded: boolean;
}

export function useEncounterPrintClinical(
  encounterId: string | null | undefined,
  patientId: string | null | undefined,
  chiefComplaint?: string | null,
): EncounterPrintClinicalResult {
  const eid = encounterId ?? '';
  const pid = patientId ?? '';

  const { data: findings = [], loaded: findingsLoaded } = usePowerSyncQuery<ClinicalFindingRow>(
    eid ? qClinicalFindingsByEncounter(eid) : qNoOp('print-findings'),
  );
  const { data: investigations = [], loaded: investigationsLoaded } =
    usePowerSyncQuery<DentalInvestigationRow>(
      eid ? qDentalInvestigationsByEncounter(eid) : qNoOp('print-investigations'),
    );
  const { data: investigationTeeth = [], loaded: investigationTeethLoaded } = usePowerSyncQuery<{
    id: string;
    tooth: string | null;
    dental_investigation_id: string | null;
  }>(pid && eid ? qDentalInvestigationTeethByPatient(pid) : qNoOp('print-inv-teeth'));

  const { data: diagnoses = [], loaded: diagnosesLoaded } =
    usePowerSyncQuery<PatientDiagnosisEntryRow>(
      eid ? qDiagnosesByEncounter(eid) : qNoOp('print-diagnoses'),
    );
  const { data: diagnosisTeeth = [], loaded: diagnosisTeethLoaded } =
    usePowerSyncQuery<DiagnosisToothRow>(
      eid ? qDiagnosisTeethByEncounter(eid) : qNoOp('print-diag-teeth'),
    );

  const { data: treatments = [], loaded: treatmentsLoaded } = usePowerSyncQuery<DentalTreatmentRow>(
    pid && eid ? qDentalTreatmentsByPatient(pid) : qNoOp('print-treatments'),
  );
  const { data: treatmentTeeth = [], loaded: treatmentTeethLoaded } = usePowerSyncQuery<{
    id: string;
    tooth: string | null;
    dental_treatment_id: string | null;
  }>(pid && eid ? qDentalTreatmentTeethByPatient(pid) : qNoOp('print-tx-teeth'));

  const { data: allRecords = [], loaded: allRecordsLoaded } =
    usePowerSyncQuery<DentalTreatmentRecordRow>(
      pid && eid ? qDentalTreatmentRecordsByPatient(pid) : qNoOp('print-tx-records'),
    );
  const { data: recordTeeth = [], loaded: recordTeethLoaded } = usePowerSyncQuery<{
    id: string;
    tooth: string | null;
    treatment_record_id: string | null;
  }>(pid && eid ? qDentalTreatmentRecordTeethByPatient(pid) : qNoOp('print-rec-teeth'));

  /**
   * Every clinical query has returned.
   *
   * `true` immediately for an encounterless prescription, where all the
   * queries no-op and there is nothing to wait for.
   */
  const loaded =
    !eid ||
    (findingsLoaded &&
      investigationsLoaded &&
      investigationTeethLoaded &&
      diagnosesLoaded &&
      diagnosisTeethLoaded &&
      treatmentsLoaded &&
      treatmentTeethLoaded &&
      allRecordsLoaded &&
      recordTeethLoaded);

  return useMemo(() => {
    if (!eid) {
      // Encounterless: compose from the complaint alone (usually also absent),
      // which yields the empty summary and omits every clinical section.
      return { ...composeEncounterPrintClinical({ chiefComplaint }), loaded };
    }

    // Treatment DONE is scoped to this encounter — the printout documents this
    // visit. The plan is patient-longitudinal, so Treatment ADVISED deliberately
    // is not: care planned at an earlier visit is still advised today.
    const encounterRecords = allRecords.filter((r) => r.encounter_id === eid);

    // Investigation tooth links are fetched patient-wide (there is no
    // per-encounter query), so restrict them to this encounter's rows before
    // pairing them positionally with `investigations`.
    const investigationIds = new Set(investigations.map((i) => i.id));
    const invTeethByInvestigation = groupToothLinks(
      investigationTeeth.filter(
        (t) => t.dental_investigation_id != null && investigationIds.has(t.dental_investigation_id),
      ),
      (r) => r.dental_investigation_id,
    );

    return {
      loaded,
      ...composeEncounterPrintClinical({
        chiefComplaint,
        findings,
        investigations,
        investigationTeeth: investigations.map((i) => invTeethByInvestigation.get(i.id) ?? []),
        diagnoses,
        teethByDiagnosis: groupTeethByDiagnosis(diagnosisTeeth),
        treatments,
        teethByTreatment: groupToothLinks(treatmentTeeth, (r) => r.dental_treatment_id),
        treatmentRecords: encounterRecords,
        teethByRecord: groupToothLinks(recordTeeth, (r) => r.treatment_record_id),
      }),
    };
  }, [
    loaded,
    eid,
    chiefComplaint,
    findings,
    investigations,
    investigationTeeth,
    diagnoses,
    diagnosisTeeth,
    treatments,
    treatmentTeeth,
    allRecords,
    recordTeeth,
  ]);
}
